import React, { useState, useEffect, useMemo } from 'react';
import { Search, Printer, Loader2, X, FileText, Calendar, Clock, MapPin } from 'lucide-react';
import { api, API_BASE } from '../utils/api';
import { formatDateThai, printReport } from '../utils/helpers';

const PermissionReport = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printLoading, setPrintLoading] = useState(false);

  // State สำหรับ Modal รายละเอียด
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personRecords, setPersonRecords] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // ⚠️ Backend จะกรองข้อมูลพนักงานและชื่อโรงเรียนให้เฉพาะของโรงเรียนตัวเองอัตโนมัติ
        const [employeesData, organizationData] = await Promise.all([
          api.getEmployees(),
          api.getOrganizations()
        ]);
        
        const activeEmployees = employeesData.filter(emp => 
          emp.is_active == 1 && 
          emp.employee_id !== 'ADMIN' && 
          emp.position !== 'ผู้ดูแลระบบ'
        );
        
        setEmployees(activeEmployees);
        setOrganization(organizationData[0] || null);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      loadReports();
    }
  }, [dateRange, selectedEmployee]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = {
        type: 'permission',
        date_from: dateRange.from,
        date_to: dateRange.to
      };
      
      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }
      
      // ⚠️ ดึงข้อมูลประวัติการขออนุญาตออกนอกสถานที่ เฉพาะบุคลากรในโรงเรียนนี้
      let results = await api.getReports(params);
      setReports(results);

    } catch (error) {
      console.error('Error loading permission reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (record) => {
    setSelectedPerson(record);
    setShowDetailModal(true);
    setLoadingDetails(true);
    setPersonRecords([]);

    try {
      const allRequests = await api.getMyPermissionRequests(record.employee_id);
      
      const fromDate = new Date(dateRange.from + 'T00:00:00');
      const toDate = new Date(dateRange.to + 'T23:59:59');

      const filtered = allRequests.filter(req => {
        const reqDate = new Date(req.request_time);
        return reqDate >= fromDate && reqDate <= toDate && req.status !== 'cancelled';
      });

      filtered.sort((a, b) => new Date(a.request_time) - new Date(b.request_time));

      setPersonRecords(filtered);
    } catch (error) {
      console.error("Error fetching details:", error);
      alert("ไม่สามารถดึงข้อมูลรายละเอียดได้");
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPerson(null);
    setPersonRecords([]);
  };

  const getPrintStyles = () => `
    @font-face {
      font-family: 'Sarabun';
      src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype');
      font-weight: 300;
      font-style: normal;
    }
    @font-face {
      font-family: 'Sarabun';
      src: url('${API_BASE}fonts/THSarabun-Bold.ttf') format('truetype');
      font-weight: 700;
      font-style: normal;
    }
    @page {
      size: A4 portrait;
      margin: 1.5cm;
    }
    body {
      font-family: 'Sarabun', sans-serif;
      font-size: 18pt;
      color: #000;
    }
    .header {
      text-align: center;
      margin-bottom: 15px;
      border-bottom: 2px solid #000;
      padding-bottom: 10px;
      font-size: 18pt;
    }
    .logo {
      max-width: 80px;
      max-height: 80px;
      margin: 0 auto 10px;
    }
    .org-name {
      font-weight: 700;
    }
    .date {
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    th, td {
      border: 1px solid #000;
      padding: 6px 4px;
      vertical-align: top;
      height: auto;
      line-height: 1.3;
    }
    th {
      background-color: #f0f0f0;
      font-weight: 700;
      text-align: center;
    }
    td {
      text-align: left;
      word-break: break-word;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    @media print {
      body { margin: 0; }
      .header {
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
    }
  `;

  const getSignatureSection = () => {
    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    return `
      <div class="signature-section" style="margin-top: 40px;">
        <table style="width: 100%; border: none; margin-bottom: 0;">
          <tr style="border: none; background-color: transparent;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none; background-color: transparent;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;
  };

  const generatePrintReport = () => {
    if (filteredReports.length === 0) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์');
      return;
    }
    setPrintLoading(true);

    const dateTitle = (dateRange.from === dateRange.to 
        ? formatDateThai(dateRange.from)
        : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`);

    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';
    const reportTitle = `<br>รายงานสรุปการขออนุญาตออกนอกสถานที่ ${dateTitle}`;

    const tableHeaders = `
      <tr>
        <th style="width: 10%;">ที่</th>
        <th style="width: 30%;">ชื่อ-นามสกุล</th>
        <th style="width: 30%;">แผนก</th>
        <th style="width: 10%;">2 ชม.</th>
        <th style="width: 10%;">ไม่กลับ</th>
        <th style="width: 10%;">กลับช้า</th>
      </tr>
    `;
    
    const tableRows = filteredReports.map((record, index) => `
      <tr>
        <td style="text-align: center;">${index + 1}</td>
        <td>${record.first_name || ''} ${record.last_name || ''}</td>
        <td style="text-align: center;">${record.department || '-'}</td>
        <td style="text-align: center;">${record.before_2pm_count || 0}</td>
        <td style="text-align: center;">${record.after_2pm_count || 0}</td>
        <td style="text-align: center;">${record.late_return_count || 0}</td>
      </tr>
    `).join('');

    const printStyles = getPrintStyles();
    const signatureSection = getSignatureSection();

    const reportContent = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${signatureSection}
    `;

    printReport(reportContent, reportTitle);
    setPrintLoading(false);
  };

  const handlePrintIndividual = () => {
    if (!selectedPerson || personRecords.length === 0) return;

    const dateTitle = (dateRange.from === dateRange.to 
        ? formatDateThai(dateRange.from)
        : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`);

    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';
    const reportTitle = `<br>รายงานการขออนุญาตออกนอกสถานที่<br> ${selectedPerson.first_name} ${selectedPerson.last_name} (${selectedPerson.department || '-'})<br>ช่วงวันที่ ${dateTitle}`;

    const summaryHtml = `
    `;

    const tableHeaders = `
      <tr>
        <th style="width: 15%;">วันที่</th>
        <th style="width: 10%;">เวลาออก</th>
        <th style="width: 10%;">เวลากลับ</th>
        <th style="width: 45%;">เหตุผล</th>
        <th style="width: 20%;">สถานะ</th>
      </tr>
    `;

    const tableRows = personRecords.map(req => {
        const dateStr = formatDateThai(req.request_time);
        const timeOut = req.departure_time 
            ? new Date(req.departure_time).toLocaleTimeString('th-TH', {hour: '2-digit', minute:'2-digit'})
            : new Date(req.request_time).toLocaleTimeString('th-TH', {hour: '2-digit', minute:'2-digit'});
        
        const timeIn = req.actual_return_time 
            ? new Date(req.actual_return_time).toLocaleTimeString('th-TH', {hour: '2-digit', minute:'2-digit'})
            : (req.status === 'no_return_required' ? '-' : 'ยังไม่กลับ');

        let statusText = '';
        if (req.status === 'returned') statusText = 'กลับแล้ว';
        else if (req.status === 'no_return_required') statusText = 'ไม่กลับ';
        else if (req.status === 'active') statusText = 'รอแจ้งกลับ';
        else statusText = req.status;

        if (req.status === 'returned' && req.expected_return_time && req.actual_return_time > req.expected_return_time) {
             statusText += ' (เกินเวลา)';
        }

        return `
          <tr>
            <td style="text-align: center;">${dateStr}</td>
            <td style="text-align: center;">${timeOut} น.</td>
            <td style="text-align: center;">${timeIn} ${timeIn !== '-' && timeIn !== 'ยังไม่กลับ' ? 'น.' : ''}</td>
            <td>${req.reason || '-'}</td>
            <td style="text-align: center;">${statusText}</td>
          </tr>
        `;
    }).join('');

    const printStyles = getPrintStyles();
    const signatureSection = getSignatureSection();

    const reportContent = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      ${summaryHtml}
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${signatureSection}
    `;

    printReport(reportContent, `${reportTitle} - ${selectedPerson.first_name}`);
  };

  const filteredReports = useMemo(() => {
    if (!searchTerm) return reports;
    const lowerSearch = searchTerm.toLowerCase();
    return reports.filter(req =>
      (req.first_name && req.first_name.toLowerCase().includes(lowerSearch)) ||
      (req.last_name && req.last_name.toLowerCase().includes(lowerSearch)) ||
      (req.department && req.department.toLowerCase().includes(lowerSearch))
    );
  }, [reports, searchTerm]);

  const activeEmployees = useMemo(() => {
    return employees
      .filter(emp => emp.employee_id !== 'ADMIN' && emp.position !== 'ผู้ดูแลระบบ')
      .sort((a, b) => a.first_name.localeCompare(b.first_name, 'th'));
  }, [employees]);
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">รายงานสรุปการขออนุญาตออก</h1>
        <button
          onClick={generatePrintReport}
          disabled={loading || filteredReports.length === 0 || printLoading || !organization} 
          className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-colors"
        >
          {printLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          <span>พิมพ์รายงานรวม</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {/* Search Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6"> 
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">จากวันที่</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">บุคลากร</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">ทุกคน</option>
              {activeEmployees.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">ค้นหา (ในผลลัพธ์)</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาชื่อ, แผนก/กลุ่มสาระฯ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Summary Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">ลำดับ</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อ-นามสกุล</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">แผนก</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">ขอออก 2 ชม.</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">ไม่กลับ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">กลับช้า</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.length > 0 ? filteredReports.map((record, index) => (
                  <tr 
                    key={`${record.employee_id}-${index}`} 
                    onClick={() => handleRowClick(record)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                    title="คลิกเพื่อดูรายละเอียด"
                  >
                    <td className="px-4 py-4 text-center text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {record.first_name} {record.last_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.department || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {record.before_2pm_count || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {record.after_2pm_count || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className={`${record.late_return_count > 0 ? 'text-red-600 font-bold' : ''}`}>
                        {record.late_return_count || 0}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      ไม่พบข้อมูลในช่วงที่เลือก
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
             {filteredReports.length > 0 && (
              <div className="mt-4 text-sm text-gray-600 flex justify-between items-center">
                <span>แสดงผลลัพธ์ {filteredReports.length} รายการ (คลิกที่แถวเพื่อดูรายละเอียด)</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      {showDetailModal && selectedPerson && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 p-2 rounded-full">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedPerson.first_name} {selectedPerson.last_name}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedPerson.department || 'ไม่ระบุแผนก'}</p>
                </div>
              </div>
              <button onClick={closeDetailModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {loadingDetails ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-500 mb-2" />
                  <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-800 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      ประวัติการขออนุญาต ({formatDateThai(dateRange.from)} - {formatDateThai(dateRange.to)})
                    </h4>
                    <button
                      onClick={handlePrintIndividual}
                      className="bg-indigo-500 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-600 flex items-center space-x-1"
                    >
                      <Printer className="h-4 w-4" />
                      <span>พิมพ์รายคน</span>
                    </button>
                  </div>

                  {personRecords.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">เวลาออก</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">เวลากลับ</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">เหตุผล</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {personRecords.map((req, idx) => {
                            const isLate = req.status === 'returned' && req.expected_return_time && req.actual_return_time > req.expected_return_time;
                            
                            const timeOut = req.departure_time 
                                ? new Date(req.departure_time).toLocaleTimeString('th-TH', {hour: '2-digit', minute:'2-digit'})
                                : new Date(req.request_time).toLocaleTimeString('th-TH', {hour: '2-digit', minute:'2-digit'});

                            return (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {formatDateThai(req.request_time)}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-gray-600">
                                  {timeOut} น.
                                </td>
                                <td className="px-4 py-3 text-sm text-center">
                                  {req.actual_return_time ? (
                                    <span className={isLate ? 'text-red-600 font-bold' : 'text-green-600'}>
                                      {new Date(req.actual_return_time).toLocaleTimeString('th-TH', {hour: '2-digit', minute:'2-digit'})} น.
                                    </span>
                                  ) : (
                                    req.status === 'no_return_required' ? '-' : <span className="text-orange-500">ยังไม่กลับ</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {req.reason}
                                </td>
                                <td className="px-4 py-3 text-sm text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    req.status === 'returned' ? (isLate ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800') :
                                    req.status === 'no_return_required' ? 'bg-gray-100 text-gray-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {req.status === 'returned' ? (isLate ? 'กลับช้า' : 'กลับแล้ว') :
                                     req.status === 'no_return_required' ? 'ออกไม่กลับ' :
                                     'รอแจ้งกลับ'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                      ไม่พบข้อมูลการขออนุญาตในช่วงวันที่เลือก
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end rounded-b-xl">
              <button
                onClick={closeDetailModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PermissionReport;